import React, { useEffect, useState } from 'react';
import {
   createLDReactProvider,
   useInitializationStatus,
   useLDClient,
} from '@launchdarkly/react-sdk';

import FeatureFlagContext from '#helper/feature-flag-context';
import { FEATURE_FLAG_DEFAULTS, FEATURE_FLAG_KEYS } from '#helper/feature-flags';

const clientSideId = process.env.GATSBY_LAUNCHDARKLY_CLIENT_SIDE_ID;

const flagKeys = Object.values(FEATURE_FLAG_KEYS);

const getLaunchDarklyFlagValues = (client) =>
   flagKeys.reduce(
      (flags, flagKey) => ({
         ...flags,
         [flagKey]: client.variation(flagKey, FEATURE_FLAG_DEFAULTS[flagKey]),
      }),
      { ...FEATURE_FLAG_DEFAULTS }
   );

const LaunchDarklyFlagBridge = ({ children }) => {
   const client = useLDClient();
   const initialization = useInitializationStatus();
   const [flags, setFlags] = useState(FEATURE_FLAG_DEFAULTS);

   useEffect(() => {
      console.log('[LD] initialization status:', initialization.status);
      if (!client || !client.isReady()) return undefined;

      const updateFlags = () => setFlags(getLaunchDarklyFlagValues(client));

      updateFlags();
      flagKeys.forEach((flagKey) => client.on(`change:${flagKey}`, updateFlags));

      return () => {
         flagKeys.forEach((flagKey) => client.off(`change:${flagKey}`, updateFlags));
      };
   }, [client, initialization.status]);

   return <FeatureFlagContext.Provider value={flags}>{children}</FeatureFlagContext.Provider>;
};

const LaunchDarklyProvider =
   typeof window !== 'undefined' && clientSideId
      ? createLDReactProvider(
           clientSideId,
           {
              kind: 'user',
              anonymous: true,
           },
           {
              ldOptions: {
                 streaming: false,
              },
           }
        )
      : null;

export const wrapWithLaunchDarkly = ({ element }) => {
   if (!LaunchDarklyProvider) {
      return (
         <FeatureFlagContext.Provider value={FEATURE_FLAG_DEFAULTS}>
            {element}
         </FeatureFlagContext.Provider>
      );
   }

   return (
      <LaunchDarklyProvider>
         <LaunchDarklyFlagBridge>{element}</LaunchDarklyFlagBridge>
      </LaunchDarklyProvider>
   );
};
